import { createServerFn } from "@tanstack/react-start";
import type { SpotMonth } from "./spotHistoryCore";

export interface ElprisReportData {
  months: SpotMonth[];
  /** Aggregerad sökstatistik från hela historiken (inga enskilda rader returneras). */
  searches: {
    total: number;
    byType: Record<string, number>;
    byArea: Record<string, number>;
    /** Antal poster med känt elområde – nämnare i elområdesfördelningen. */
    areaTotal: number;
    /** Tidigaste och senaste tidpunkt statistiken täcker. */
    since: string | null;
    until: string | null;
  };
  /** SCB: andel (%) av elhandelskunder per avtalstyp, riket, senaste månad. */
  scb: { period: string; byType: Record<string, number> } | null;
  generatedAt: string;
}

const ZONES = ["SE1", "SE2", "SE3", "SE4"];

/** Klickets svenska avtalsnamn översätts till samma nycklar som feeden använder. */
const CLICK_TYPE: Record<string, string> = {
  "fast pris": "fast",
  "rörligt pris": "rorligt",
  "rörligt": "rorligt",
  "månadspris": "manadspris",
  "kvartspris": "kvartspris",
};

/**
 * Publik rapportdata: månadsspot per elområde + aggregerad sökstatistik över
 * hela historiken och klick på jämförda avtal slås samman, och endast
 * fördelningar (antal och andelar) lämnar servern.
 */
export const getElprisReport = createServerFn({ method: "GET" }).handler(
  async (): Promise<ElprisReportData> => {
    const { createClient } = await import("@supabase/supabase-js");
    const { fetchSpotMonthsWith } = await import("./spotHistoryCore");
    const key = process.env["SUPABASE_PUBLISHABLE_KEY"]!;
    const sb = createClient(process.env["SUPABASE_URL"]!, key, {
      auth: { persistSession: false, autoRefreshToken: false },
    });
    const months = await fetchSpotMonthsWith(sb);

    const byType: Record<string, number> = {};
    const byArea: Record<string, number> = {};
    let total = 0;
    let areaTotal = 0;
    let since: string | null = null;
    let until: string | null = null;

    const add = (rawType: string | null, area: string | null, at: string | null) => {
      total++;
      const t = (rawType ?? "okänd").toLowerCase().trim();
      byType[t] = (byType[t] ?? 0) + 1;
      if (area && ZONES.includes(area)) {
        byArea[area] = (byArea[area] ?? 0) + 1;
        areaTotal++;
      }
      if (at) {
        if (!since || at < since) since = at;
        if (!until || at > until) until = at;
      }
    };

    // Sökningar loggade i livefeeden, hela historiken.
    try {
      for (let from = 0; from < 50000; from += 1000) {
        const { data, error } = await sb
          .from("compare_feed")
          .select("elomrade, avtalstyp, created_at")
          .range(from, from + 999);
        if (error || !data) break;
        for (const r of data) {
          add(
            (r.avtalstyp as string | null) ?? null,
            (r.elomrade as string | null) ?? null,
            (r.created_at as string | null) ?? null,
          );
        }
        if (data.length < 1000) break;
      }
    } catch {
      /* sökstatistik är valfri */
    }

    // Historik före livefeeden: avtalstyp och elområde för varje klick på ett
    // jämfört avtal. Tabellen är adminläst, så aggregeringen körs med privilegerad
    // klient och bara fördelningarna returneras.
    try {
      const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
      for (let from = 0; from < 100000; from += 1000) {
        const { data, error } = await supabaseAdmin
          .from("offer_clicks")
          .select("elomrade, contract_type, created_at")
          .range(from, from + 999);
        if (error || !data) break;
        for (const r of data) {
          const raw = ((r.contract_type as string | null) ?? "").toLowerCase().trim();
          add(CLICK_TYPE[raw] ?? (raw || null), (r.elomrade as string | null) ?? null, (r.created_at as string | null) ?? null);
        }
        if (data.length < 1000) break;
      }
    } catch {
      /* klickhistorik är valfri */
    }

    // SCB:s publika avtalsfördelning (andel kunder per avtalstyp, hela Sverige) från senaste ögonblicksbilden.
    let scb: ElprisReportData["scb"] = null;
    try {
      const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
      const { data: snap } = await supabaseAdmin
        .from("scb_snapshots").select("payload").neq("source", "kommun")
        .order("fetched_at", { ascending: false }).limit(1).maybeSingle();
      const rows = ((snap?.payload as { avtalstyp?: { rows?: { period: string; key: { Avtalstyp: string; Elomrade: string }; value: number | null }[] | null } } | null)?.avtalstyp?.rows ?? [])
        .filter((r) => r.key.Elomrade === "00" && r.value != null);
      const period = rows.map((r) => r.period).sort().at(-1);
      if (period) {
        const byType: Record<string, number> = {};
        for (const r of rows) if (r.period === period) byType[r.key.Avtalstyp] = r.value!;
        scb = { period, byType };
      }
    } catch {
      /* SCB-jämförelse är valfri */
    }
    return {
      months,
      searches: { total, byType, byArea, areaTotal, since, until },
      scb,
      generatedAt: new Date().toISOString(),
    };
  },
);
